<template>
  <div class="flex flex-col gap-16" data-qa="user-informations">
    <h2 class="heading-3">{{ i18n(translations.title) }}</h2>
    <RevCard class="flex items-start justify-between gap-16 p-24">
      <RevForm
        class="w-full"
        :initial-values
        method="POST"
        name="user-informations-form"
        :validate="validators"
        @on-submit-failed="handleSubmitFailed"
        @submit="handleSubmit"
      >
        <template #default="{ values, errors }">
          <UserInformationFields
            v-model:birthdate="values.birthdate"
            v-model:national-id="values.nationalId"
            :country="values.country as Country"
            :errors
            :with-birthdate="userInformationStore.isFormRequired"
            :with-national-id="userInformationStore.isFormRequired"
          />
        </template>
      </RevForm>
    </RevCard>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Country } from '@backmarket/http-api'
import { useUserInformationStore } from '@backmarket/nuxt-layer-checkout/stores/userInformationStore'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevCard } from '@ds/components/Card'
import { RevForm, makeValidate } from '@ds/components/Form'

import translations from './UserInformations.translations'

const props = defineProps<{
  country: string
  nationalId?: string
}>()

const emit = defineEmits<{
  submit: [values: FormValues]
  'submit-failed': [errors: Record<string, string>]
}>()

const i18n = useI18n()
const userInformationStore = useUserInformationStore()

type FormValues = {
  birthdate: string
  nationalId: string
  country: string
}

const validators = makeValidate<FormValues>({
  ...insertIf(userInformationStore.isFormRequired, {
    birthdate: InputAddressBirthdateValidators(i18n),
    nationalId: InputAddressNationalIdValidators(i18n),
  }),
})

const initialValues = computed<FormValues>(() => ({
  birthdate: userInformationStore.birthdate ?? '',
  nationalId: props.nationalId ?? '',
  country: props.country,
}))

function handleSubmit(values: FormValues) {
  emit('submit', {
    country: values.country,
    ...insertIf(userInformationStore.isFormRequired, {
      birthdate: values.birthdate,
      nationalId: values.nationalId,
    }),
  } as FormValues)
}

function handleSubmitFailed({
  errors,
}: {
  errors: Partial<Record<keyof FormValues, string>>
}) {
  emit('submit-failed', errors)
}
</script>
